import {useEffect, useState} from 'react'
import useMyOrderPreferencesWithDefault, {
  UseOrderPreferencesWebsite
} from './useMyOrderPreferencesWithDefault'
import useTimeField, {
  deliveryTypeIsField,
  DELIVERY_VALUES_ENUM
} from '@page-components/Checkout/Checkout/Delivery/Duration/Update/TimeField/useTimeField'
import usePrevious from 'use-previous'
import useWebsiteId from '@hooks/useWebsiteId'
import useApolloQuery from '@packages/apollo-hooks/lib/useApolloQuery'
import dayjs from 'dayjs'
import gql from 'graphql-tag'
import isServerSide from '@helpers/misc/isServerSide'

/**
 * This fetches the time from the preferences and uses that to fetch the timeOptions
 */
const usePreferredTimeOptions = () => {
  const websiteId = useWebsiteId()
  const {userPreferences} = useApolloQuery({
    query: gql`
      query getWebsiteTime($websiteId: ID) {
        userPreferences(websiteId: $websiteId) {
          _id
          time
        }
      }
    `,
    partial: true,
    variables: {websiteId: websiteId ? websiteId : ''},
    omit: !websiteId || isServerSide()
  })

  const time = userPreferences && userPreferences.time
  const days = time === 'now' ? 0 : time && Number(time.split('+')[1])
  const date =
    days != null &&
    dayjs()
      .add(days, 'days')
      .startOf('day')
      .add(3, 'hours') // to fix tz issues
      .format()

  const {preferences} = useApolloQuery({
    query: gql`
      query getWebsiteTimeOptions($websiteId: ID, $date: Date) {
        preferences: userPreferences(websiteId: $websiteId) {
          _id
          timeOptions(date: $date) {
            label
            value
            disabled
          }
        }
      }
    `,
    variables: {
      websiteId: websiteId ? websiteId : '',
      date
    },
    omit: !date || isServerSide(),
    partial: true
  })

  if (!websiteId || !userPreferences || !userPreferences.time || !preferences) return null
  return preferences.timeOptions
}

const getDeliveryTypeFromOption = deliveryTypeOption => {
  const isArray = Array.isArray(deliveryTypeOption)
  if (isArray && deliveryTypeOption.length) {
    return deliveryTypeOption[0].value
  }
  if (deliveryTypeIsField(deliveryTypeOption)) {
    return deliveryTypeOption.value
  }
  return null
}

export default function useDeliveryOptions(website: UseOrderPreferencesWebsite) {
  const {preferences: queriedPrefs} = useMyOrderPreferencesWithDefault(website)
  let preferences = undefined
  if (queriedPrefs) {
    preferences = {...queriedPrefs, store: queriedPrefs?.storeWithSlots}
  }

  const [deliveryType, setDeliveryType] = useState(DELIVERY_VALUES_ENUM.deliver_now)
  const {deliveryType: deliveryTypeOption} = useTimeField({preferences, save: () => {}})
  const [hasChangedDeliveryType, setHasChangedDeliveryType] = useState(false)
  const previousDeliveryType = usePrevious(DELIVERY_VALUES_ENUM.deliver_now)

  // Initialize the context deliveryType value if possible
  useEffect(() => {
    if (!deliveryTypeOption) return

    const newDeliveryType = getDeliveryTypeFromOption(deliveryTypeOption)
    if (
      preferences &&
      !hasChangedDeliveryType &&
      newDeliveryType &&
      deliveryType !== newDeliveryType
    ) {
      setDeliveryType(newDeliveryType)
    }
  }, [preferences, hasChangedDeliveryType, deliveryType, deliveryTypeOption])

  useEffect(() => {
    if (!hasChangedDeliveryType && previousDeliveryType !== deliveryType) {
      setHasChangedDeliveryType(true)
    }
  }, [hasChangedDeliveryType, deliveryType, previousDeliveryType])

  const [timeOptions, setTimeOptions] = useState(null)
  const timeOptionsQuery = usePreferredTimeOptions()
  const [hasChangedTimeOptions, setHasChangedTimeOptions] = useState(false)
  const previousTimeOptions = usePrevious(timeOptions)

  // Initialize the context timeOptions value if possible
  useEffect(() => {
    if (timeOptionsQuery && !hasChangedTimeOptions) {
      const queryOptions = timeOptionsQuery.map(option => ({
        ...option,
        isDisabled: option.disabled
      }))

      const isAnyOptionEnabled = queryOptions.some(option => !option.disabled)
      const options = isAnyOptionEnabled ? queryOptions : []

      setTimeOptions(options)
    }
  }, [timeOptionsQuery, hasChangedTimeOptions])

  useEffect(() => {
    if (!hasChangedTimeOptions && timeOptions !== previousTimeOptions) {
      setHasChangedTimeOptions(true)
    }
  }, [hasChangedTimeOptions, timeOptions, previousTimeOptions])

  return {timeOptions, setTimeOptions, deliveryType, setDeliveryType}
}
